"use client"

import * as React from "react"
import { Link } from "react-router-dom"
import { AlertTriangleIcon } from "lucide-react"
import type { BranchFinanceBoardFunds } from "@ibada/shared-types/overview"

import { HomeTile, HomeTileEmpty, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import { BudgetLineType } from "@/features/budgeting/model/budget-enums"
import { BudgetRowState, type BudgetRow } from "@/features/finance/overview/branch-f1/lib/branch-f1-format"
import { useTranslations } from "@/i18n"
import { formatCurrency } from "@/shared/lib/formatting/currency"
import { cn } from "@ibada/ui"

const ROW_BAR_CLASS: Record<BudgetRowState, string> = {
  [BudgetRowState.OnPace]: "bg-emerald-600 dark:bg-emerald-500",
  [BudgetRowState.AheadOfPace]: "bg-amber-500 dark:bg-amber-400",
  [BudgetRowState.Over]: "bg-red-600 dark:bg-red-500",
}

const ROW_NOTE_CLASS: Record<BudgetRowState, string> = {
  [BudgetRowState.OnPace]: "text-muted-foreground",
  [BudgetRowState.AheadOfPace]: "font-bold text-amber-700 dark:text-amber-300",
  [BudgetRowState.Over]: "font-bold text-red-700 dark:text-red-300",
}

/** A planned-vs-realised bar with the time-elapsed marker. */
function PaceBar({ used, pace, state }: { used: number | null; pace: number | null; state: BudgetRowState }) {
  return (
    <span className='relative block h-2 rounded-[4px] bg-muted'>
      <span className={cn("block h-full rounded-[4px]", ROW_BAR_CLASS[state])} style={{ width: `${Math.min(100, Math.max(0, used ?? 0))}%` }} />
      {pace !== null ? (
        <span aria-hidden data-testid='branch-budget-pace' className='absolute -top-[3px] -bottom-[3px] w-0.5 rounded-[1px] bg-foreground' style={{ left: `${pace}%` }} />
      ) : null}
    </span>
  )
}

/**
 * Budget (F1): the five budget widgets merged into one tile. Planned against realised to date for the largest
 * lines, a marker at the share of the budget's time already elapsed, a switch between expenses and income, and a
 * warning when an expense line runs ahead of that pace. Every amount and utilisation is the server's (ADR-0011).
 */
export function BranchBudgetTile({
  title,
  pace,
  expenseRows,
  incomeRows,
  currency,
  link,
}: {
  title: string
  pace: number | null
  expenseRows: readonly BudgetRow[]
  incomeRows: readonly BudgetRow[]
  currency: string
  link: HomeTileLink | null
}) {
  const t = useTranslations("finance.overview")
  const [type, setType] = React.useState<BudgetLineType>(expenseRows.length > 0 ? BudgetLineType.Expense : BudgetLineType.Income)
  const rows = type === BudgetLineType.Expense ? expenseRows : incomeRows
  return (
    <HomeTile
      title={title}
      subtitle={pace === null ? t("branch.budget.subtitleNoPace") : t("branch.budget.subtitle", { pace })}
      link={link}
      testId='branch-tile-budget'
    >
      <div role='group' aria-label={t("branch.budget.switch")} className='flex gap-1.5'>
        {[BudgetLineType.Expense, BudgetLineType.Income].map((option) => (
          <button
            key={option}
            type='button'
            aria-pressed={type === option}
            onClick={() => setType(option)}
            className={cn(
              "rounded-full px-2.5 py-0.5 text-xs font-bold focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
              type === option ? "bg-foreground text-background" : "bg-muted text-foreground hover:bg-muted/70",
            )}
          >
            {t(`branch.budget.type.${option}`)}
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className='text-[13px] text-muted-foreground'>{t(`branch.budget.noLines.${type}`)}</p>
      ) : (
        <ul className='flex flex-col gap-2.5'>
          {rows.map((row) => (
            <li key={row.id} data-testid='branch-budget-row' data-state={row.state} className='flex flex-col gap-1'>
              <span className='flex items-baseline gap-2 text-[12.5px]'>
                <span className='truncate font-semibold text-foreground'>{row.label}</span>
                <span className='ml-auto shrink-0 text-muted-foreground tabular-nums'>
                  <b className='text-foreground'>{formatCurrency(row.actual, currency)}</b>
                  {` / ${formatCurrency(row.planned, currency)}`}
                </span>
              </span>
              <PaceBar used={row.utilizationPct} pace={pace} state={row.state} />
              {row.state === BudgetRowState.OnPace ? null : (
                <span className={cn("text-[11.5px]", ROW_NOTE_CLASS[row.state])}>
                  {row.state === BudgetRowState.Over
                    ? t("branch.budget.over", { used: Math.round(row.utilizationPct ?? 0) })
                    : t("branch.budget.ahead", { used: Math.round(row.utilizationPct ?? 0), pace: pace ?? 0 })}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </HomeTile>
  )
}

/** One color per listed fund, the remainder in slate (the F3 share palette). */
const FUND_COLORS = ["bg-emerald-600 dark:bg-emerald-500", "bg-teal-500 dark:bg-teal-400", "bg-violet-600 dark:bg-violet-400", "bg-amber-500 dark:bg-amber-400"]
const OTHER_FUND_COLOR = "bg-slate-300 dark:bg-slate-600"

/** Funds (F1): what the branch's funds may still commit, in total, as a segmented bar and per fund. */
export function BranchFundsTile({ funds, currency, link }: { funds: BranchFinanceBoardFunds; currency: string; link: HomeTileLink | null }) {
  const t = useTranslations("finance.overview")
  const segments = [
    ...funds.rows.map((row, index) => ({ id: row.fundId, name: row.name, amount: row.available, bp: row.shareBp, color: FUND_COLORS[index] ?? OTHER_FUND_COLOR })),
    ...(funds.otherShareBp > 0 ? [{ id: "other", name: t("church.share.others"), amount: funds.other, bp: funds.otherShareBp, color: OTHER_FUND_COLOR }] : []),
  ]
  return (
    <HomeTile title={t("widgets.branchFunds.title")} subtitle={t("widgets.branchFunds.subtitle")} link={link} testId='branch-tile-funds'>
      {funds.fundCount === 0 ? (
        <HomeTileEmpty message={t("branch.funds.empty")} />
      ) : (
        <>
          <span className='flex items-baseline gap-2'>
            <span data-testid='branch-funds-total' className='text-[1.65rem] leading-none font-extrabold tracking-tight whitespace-nowrap tabular-nums'>
              {formatCurrency(funds.total, currency)}
            </span>
            <span className='text-xs text-muted-foreground'>{t(funds.fundCount === 1 ? "branch.funds.countOne" : "branch.funds.countOther", { count: funds.fundCount })}</span>
          </span>
          {segments.length > 0 ? (
            <div aria-hidden className='flex gap-0.5'>
              {segments.map((segment) => (
                <span key={segment.id} className={cn("h-3 rounded-[3px]", segment.color)} style={{ width: `${segment.bp / 100}%` }} />
              ))}
            </div>
          ) : null}
          <ul className='flex flex-col gap-1.5'>
            {segments.map((segment) => (
              <li key={segment.id} data-testid='branch-fund-row' className='flex items-center gap-2 text-[12.5px]'>
                <span aria-hidden className={cn("size-2.5 shrink-0 rounded-[3px]", segment.color)} />
                <span className='truncate text-muted-foreground'>{segment.name}</span>
                <b className='ml-auto shrink-0 tabular-nums'>{formatCurrency(segment.amount, currency)}</b>
              </li>
            ))}
          </ul>
        </>
      )}
    </HomeTile>
  )
}

/** A fulfilment ring: a ratio the server computed, drawn as an arc. */
function Ring({ percent }: { percent: number }) {
  const radius = 30
  const circumference = 2 * Math.PI * radius
  const filled = Math.min(100, Math.max(0, percent)) / 100
  return (
    <svg viewBox='0 0 74 74' className='size-[74px] shrink-0' aria-hidden>
      <circle cx='37' cy='37' r={radius} fill='none' strokeWidth='8' className='stroke-muted' />
      <circle
        cx='37'
        cy='37'
        r={radius}
        fill='none'
        strokeWidth='8'
        strokeLinecap='round'
        strokeDasharray={`${filled * circumference} ${circumference}`}
        transform='rotate(-90 37 37)'
        className='stroke-violet-600 dark:stroke-violet-400'
      />
      <text x='37' y='41' textAnchor='middle' fontSize='14' fontWeight='800' className='fill-foreground'>
        {`${Math.round(percent)}%`}
      </text>
    </svg>
  )
}

export interface PledgeTopRow {
  id: string
  name: string
  received: string
  pledged: string
}

/** Pledges (F1): collected against promised for the open money pledges, the late ones, and the two largest. */
export function BranchPledgesTile({
  received,
  pledged,
  fulfillment,
  count,
  late,
  top,
  currency,
  link,
  lateLink,
}: {
  received: string
  pledged: string
  fulfillment: number
  count: number
  late: number
  top: readonly PledgeTopRow[]
  currency: string
  link: HomeTileLink | null
  lateLink: HomeTileLink | null
}) {
  const t = useTranslations("finance.overview")
  return (
    <HomeTile title={t("widgets.branchPledges.title")} subtitle={t("widgets.branchPledges.subtitle")} link={link} testId='branch-tile-pledges'>
      <div className='flex items-center gap-3.5'>
        <Ring percent={fulfillment} />
        <span className='flex min-w-0 flex-col gap-0.5 text-[13px]'>
          <b className='tabular-nums'>{t("branch.pledges.received", { amount: formatCurrency(received, currency) })}</b>
          <span className='text-muted-foreground tabular-nums'>{t("branch.pledges.pledged", { amount: formatCurrency(pledged, currency) })}</span>
          <span className='text-muted-foreground'>{t(count === 1 ? "branch.pledges.countOne" : "branch.pledges.countOther", { count })}</span>
        </span>
      </div>
      {late > 0 ? (
        <div data-testid='branch-pledges-late' className='flex items-center gap-2 rounded-[10px] bg-orange-50 px-2.5 py-2 text-[12.5px] dark:bg-orange-950/40'>
          <AlertTriangleIcon aria-hidden className='size-4 shrink-0 text-orange-700 dark:text-orange-300' />
          <span>
            <b>{t(late === 1 ? "branch.pledges.lateOne" : "branch.pledges.lateOther", { count: late })}</b>
          </span>
          {lateLink ? (
            <Link to={lateLink.href} className='ml-auto font-bold text-orange-700 hover:underline dark:text-orange-300'>
              {lateLink.label}
            </Link>
          ) : null}
        </div>
      ) : null}
      <ul className='flex flex-col'>
        {top.map((row) => (
          <li key={row.id} data-testid='branch-pledge-row' className='flex gap-2 border-t border-border/60 pt-1.5 pb-1 text-[12.5px]'>
            <span className='truncate text-muted-foreground'>{row.name}</span>
            <b className='ml-auto shrink-0 tabular-nums'>{`${formatCurrency(row.received, currency)} / ${formatCurrency(row.pledged, currency)}`}</b>
          </li>
        ))}
      </ul>
    </HomeTile>
  )
}

